import {
  createContext,
  type HTMLAttributes,
  type PropsWithChildren,
  useContext,
  useId,
} from 'react';
import { cn } from '../../../lib/styles';
import type { TabsPanelProps } from './types';

type PanelAttributes = Pick<HTMLAttributes<HTMLDivElement>, 'role' | 'aria-labelledby'> & {
  id?: string;
};

type TabsContextValue = {
  value: string;
  /** Whether a strip is rendered above the panels, so a panel knows to clear it. */
  hasStrip: boolean;
  /**
   * Attributes for the wrapper a panel renders, so a panel only claims tabpanel
   * semantics where the platform's strip supplies the tab to match it.
   */
  panelAttributes: (value: string) => PanelAttributes;
};

const TabsContext = createContext<TabsContextValue | undefined>(undefined);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs.Panel must be used within Tabs');
  }
  return context;
}

/**
 * Stable ids for a tab and its panel, so the strip and the panel can point at each
 * other regardless of which platform renders them.
 */
export function useTabIds() {
  const uid = useId();

  return {
    tabId: (value: string) => `${uid}tab-${value}`,
    panelId: (value: string) => `${uid}panel-${value}`,
  };
}

/**
 * Column frame and shared context. Each platform renders its own tab strip into
 * the frame; the panels come from the consumer as `Tabs.Panel` children.
 */
export function TabsFrame({ children, ...context }: PropsWithChildren<TabsContextValue>) {
  return (
    <TabsContext.Provider value={context}>
      <div className="flex flex-col">{children}</div>
    </TabsContext.Provider>
  );
}

export function TabsPanel({ value: panelValue, className, children }: TabsPanelProps) {
  const { value, hasStrip, panelAttributes } = useTabsContext();

  if (value !== panelValue) {
    return null;
  }

  return (
    <div
      {...panelAttributes(panelValue)}
      // The primitive owns the gap between the strip and its content, so panels sit
      // the same distance below the tabs on every platform.
      className={cn('flex flex-1 flex-col', hasStrip && 'pt-4', className)}
    >
      {children}
    </div>
  );
}
